import type {
  AdminCapabilities,
  AdminDashboard,
  AdminStrategyChat,
  AdminPlan,
  AdminAuditEntry,
  AdminCreditPack,
  UpdateCreditPackRequest,
  CreateStripePriceRequest,
  CreateStripePriceResponse,
  PlanStripePrices,
  AdminUserDetail,
  AdminUserFillsPage,
  AdminUserRow,
  AdminUsersPage,
  AiCosts,
  AiCostsParams,
  GrantOverrideRequest,
  GrantOverrideResponse,
  InfraMetrics,
  InfraOverview,
  InfraWindow,
  SubscriptionOverrideSummary,
  UpdatePlanRequest,
  UserFillsParams,
  UsersListParams,
  UsersSortBy,
} from '@core/api/adminTypes';
import {
  mockAiCosts, mockCreditPacks, mockDashboard, mockInfraMetrics, mockInfraOverview, mockPlans,
  mockStrategyChat, mockUserDetail, mockUserFills, mockUserRow, mockUsers, MOCK_SESSIONS_SINCE,
} from './mockData';

/** Stable pseudo-id source so a mock-created price id doesn't change on re-render. */
function hashLike(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/** Simulated latency so skeletons/loading states are visible in the demo. */
function delay<T>(value: T, ms = 280): Promise<T> {
  return new Promise(resolve => setTimeout(() => resolve(value), ms));
}

/** Users disabled during this session (mock mutation) — drives the detail page + list. */
const disabled = new Set<string>();

/** Users whose concurrent login was marked Established during this session (mock mutation). */
const concurrentEstablished = new Set<string>();

/** AI credits granted during this session (mock mutation) — added on top of the base balance. */
const aiCreditsGranted = new Map<string, number>();

/**
 * Tier overrides mutated during this session. A present value = an active override; an explicit `null`
 * = the seeded override was revoked (distinct from "never touched", which falls through to the seed).
 */
const overrides = new Map<string, SubscriptionOverrideSummary | null>();

/** Plan-config edits made during this session, keyed by tier. */
const planEdits = new Map<string, UpdatePlanRequest>();

/** Credit-pack edits made during this session, keyed by pack code. */
const packEdits = new Map<string, UpdateCreditPackRequest>();

/**
 * Admin writes made during this session, keyed by user id (newest first) — the mock equivalent of the
 * backend's audit trail, so the activity card visibly grows as you act on a user.
 */
const sessionTrail = new Map<string, AdminAuditEntry[]>();

function recordAdminAction(id: string, action: string, topic: string, detail: string | null): void {
  const entry: AdminAuditEntry = { at: new Date().toISOString(), action, topic, detail, actor: 'admin:mock' };
  sessionTrail.set(id, [entry, ...(sessionTrail.get(id) ?? [])]);
}

export const mockApi = {
  /** Demo/dev backend serves every feature, so every capability is true (UI shows all sections). */
  capabilities(): Promise<AdminCapabilities> {
    return delay({
      approvals: true, cqg: true, registrationAudit: true, actionFlags: true, positions: true,
      aiCredits: true, aiUsage: true, infra: true, subscriptions: true, creditPacks: true,
      aiStrategies: true, compliance: true,
    });
  },

  dashboard(from: string, to: string): Promise<AdminDashboard> {
    return delay(mockDashboard(from, to));
  },

  aiCosts(params: AiCostsParams): Promise<AiCosts> {
    return delay(mockAiCosts(params));
  },

  infra(): Promise<InfraOverview> {
    return delay(mockInfraOverview());
  },

  infraMetrics(window: InfraWindow): Promise<InfraMetrics> {
    return delay(mockInfraMetrics(window));
  },

  users(params: UsersListParams): Promise<AdminUsersPage> {
    const { from, to, page, pageSize, search, sortBy = 'createdAt', sortDir = 'desc' } = params;
    const rows = mockUsers()
      // Disabled users stay in the roster, flagged (matches the real backend).
      .map(u => ({ ...mockUserRow(u, from, to), isDisabled: disabled.has(u.userId) }));

    const term = search?.trim().toLowerCase();
    const filtered = term
      ? rows.filter(r => r.email.toLowerCase().includes(term) || r.userId.toLowerCase().includes(term))
      : rows;

    const sorted = [...filtered].sort((a, b) => cmp(a, b, sortBy) * (sortDir === 'asc' ? 1 : -1));

    const total = sorted.length;
    const start = (page - 1) * pageSize;
    const items = sorted.slice(start, start + pageSize);
    return delay({ items, total, page, pageSize, loginRecordingSince: MOCK_SESSIONS_SINCE });
  },

  userDetail(id: string, from: string, to: string): Promise<AdminUserDetail> {
    const user = mockUsers().find(u => u.userId === id);
    if (!user) return Promise.reject(new Error('User not found'));
    const detail = mockUserDetail(user, from, to);
    if (disabled.has(id)) detail.profile.isDeleted = true;
    detail.profile.concurrentLoginEstablished = concurrentEstablished.has(id);
    detail.aiCreditBalance = (detail.aiCreditBalance ?? 0) + (aiCreditsGranted.get(id) ?? 0);
    // Overlay this session's override grant/revoke, recomputing the effective tier + entitlement the same
    // way the backend does (an active override wins in both directions and always entitles).
    if (detail.subscription && overrides.has(id)) {
      const ov = overrides.get(id) ?? null;
      const sub = detail.subscription;
      detail.subscription = {
        ...sub,
        override: ov,
        tier: ov?.tier ?? sub.subscriptionTier,
        isEntitled: ov != null || sub.status === 'Active',
      };
    }
    detail.adminActivity = [...(sessionTrail.get(id) ?? []), ...detail.adminActivity];

    // Keep the AI panel coherent with this session's top-ups: the grant lands on the ledger, lifts the
    // balance, and clears the pending request — exactly what the real grant does.
    const granted = aiCreditsGranted.get(id) ?? 0;
    if (detail.aiActivity && granted > 0) {
      const a = detail.aiActivity;
      detail.aiActivity = {
        ...a,
        creditBalance: (a.creditBalance ?? 0) + granted,
        creditsGranted: a.creditsGranted + granted,
        pendingCreditRequest: false,
        ledger: [
          {
            at: new Date().toISOString(), delta: granted, reason: 2, reasonLabel: 'Admin grant',
            strategyId: null, strategyName: null, isRetry: false,
          },
          ...a.ledger,
        ],
      };
    }
    return delay(detail);
  },

  grantOverride(id: string, body: GrantOverrideRequest): Promise<GrantOverrideResponse> {
    const grantedAt = new Date().toISOString();
    const expiresAt = body.duration === 'OneMonth'
      ? new Date(Date.now() + 30 * 86400_000).toISOString()
      : null;
    overrides.set(id, {
      tier: body.tier, expiresAt, reason: body.reason, grantedBy: 'admin:mock', grantedAt,
    });
    const window = expiresAt ? `until ${expiresAt.slice(0, 10)}` : 'permanent';
    recordAdminAction(id, 'OverrideGranted', 'override', `${body.tier} · ${window} · "${body.reason}"`);
    return delay({ id: `ovr_${id}`, tier: body.tier, expiresAt, grantedBy: 'admin:mock', grantedAt }, 320);
  },

  revokeOverride(id: string): Promise<void> {
    const revoked = overrides.get(id);
    overrides.set(id, null);
    recordAdminAction(id, 'OverrideRevoked', 'override', revoked?.tier ?? null);
    return delay(undefined, 320);
  },

  plans(): Promise<AdminPlan[]> {
    const base = mockPlans();
    return delay(base.map(p => {
      const edit = planEdits.get(p.tier);
      return edit ? { ...p, ...edit, updatedAt: new Date().toISOString() } : p;
    }));
  },

  planStripePrices(tier: string): Promise<PlanStripePrices> {
    const plan = mockPlans().find(p => p.tier === tier);
    const edit = planEdits.get(tier);
    const monthlyCents = edit?.monthlyPriceCents ?? plan?.monthlyPriceCents ?? 0;
    const annualCents = edit?.annualPriceCents ?? plan?.annualPriceCents ?? 0;
    const monthlyId = edit?.stripePriceIdMonthly ?? plan?.stripePriceIdMonthly ?? null;
    const annualId = edit?.stripePriceIdAnnual ?? plan?.stripePriceIdAnnual ?? null;

    // Demo the DRIFT case on monthly (Stripe says something else) and the match case on annual.
    const live = (id: string | null, cents: number, interval: string, drift: number) =>
      id ? {
        id, unitAmountCents: cents + drift, currency: 'usd', interval,
        active: true, productId: 'prod_MockPro', liveMode: false,
      } : null;

    return delay({
      tier,
      canCreatePrices: true,
      prices: [
        {
          cadence: 'monthly', priceId: monthlyId, displayCents: monthlyCents,
          price: live(monthlyId, monthlyCents, 'month', 500),
          matches: monthlyId ? false : null, error: null,
        },
        {
          cadence: 'annual', priceId: annualId, displayCents: annualCents,
          price: live(annualId, annualCents, 'year', 0),
          matches: annualId ? true : null, error: null,
        },
      ],
    });
  },

  createStripePrice(tier: string, body: CreateStripePriceRequest): Promise<CreateStripePriceResponse> {
    const base = mockPlans().find(p => p.tier === tier);
    if (!base) return Promise.reject(new Error('Unknown tier'));
    const newId = `price_mock${Math.abs(hashLike(tier + body.cadence + body.amountCents))}`;
    const prev = planEdits.get(tier) ?? {
      name: base.name, description: base.description, features: base.features,
      monthlyPriceCents: base.monthlyPriceCents, annualPriceCents: base.annualPriceCents,
      stripePriceIdMonthly: base.stripePriceIdMonthly, stripePriceIdAnnual: base.stripePriceIdAnnual,
      aiCreditsPerMonth: base.aiCreditsPerMonth, isActive: base.isActive,
      isFeatured: base.isFeatured, sortOrder: base.sortOrder,
    };
    const next = body.cadence === 'annual'
      ? { ...prev, stripePriceIdAnnual: newId }
      : { ...prev, stripePriceIdMonthly: newId };
    planEdits.set(tier, next);

    return delay({
      price: {
        id: newId, unitAmountCents: body.amountCents, currency: 'usd',
        interval: body.cadence === 'annual' ? 'year' : 'month',
        active: true, productId: 'prod_MockPro', liveMode: false,
      },
      cadence: body.cadence,
      archivedOldPrice: body.archiveOldPrice,
      oldPriceId: body.cadence === 'annual' ? prev.stripePriceIdAnnual : prev.stripePriceIdMonthly,
      plan: { ...base, ...next, updatedAt: new Date().toISOString() },
    }, 380);
  },

  creditPacks(): Promise<AdminCreditPack[]> {
    const base = mockCreditPacks();
    return delay(base.map(p => {
      const edit = packEdits.get(p.code);
      return edit ? { ...p, ...edit, updatedAt: new Date().toISOString() } : p;
    }).sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code)));
  },

  updateCreditPack(code: string, body: UpdateCreditPackRequest): Promise<AdminCreditPack> {
    packEdits.set(code, body);
    const base = mockCreditPacks().find(p => p.code === code);
    if (!base) return Promise.reject(new Error('Unknown pack'));
    return delay({ ...base, ...body, updatedAt: new Date().toISOString() }, 320);
  },

  updatePlan(tier: string, body: UpdatePlanRequest): Promise<AdminPlan> {
    planEdits.set(tier, body);
    const base = mockPlans().find(p => p.tier === tier);
    if (!base) return Promise.reject(new Error('Unknown tier'));
    return delay({ ...base, ...body, updatedAt: new Date().toISOString() }, 320);
  },

  disableUser(id: string): Promise<void> {
    disabled.add(id);
    recordAdminAction(id, 'UserDisabled', 'user', null);
    return delay(undefined, 350);
  },

  enableUser(id: string): Promise<void> {
    disabled.delete(id);
    recordAdminAction(id, 'UserEnabled', 'user', null);
    return delay(undefined, 350);
  },

  setConcurrentLogin(id: string, established: boolean): Promise<void> {
    if (established) concurrentEstablished.add(id);
    else concurrentEstablished.delete(id);
    recordAdminAction(id, established ? 'ConcurrentLoginMarked' : 'ConcurrentLoginCleared', 'user', null);
    return delay(undefined, 300);
  },

  /**
   * One strategy's Ask-the-AI turns, oldest first. Deterministic in `(userId, strategyId)`; many
   * strategies legitimately have none.
   */
  /** One page of the user's executions. Rejects for an unknown id, as the real endpoint 404s. */
  userFills(id: string, params: UserFillsParams): Promise<AdminUserFillsPage> {
    const user = mockUsers().find(u => u.userId === id);
    if (!user) return Promise.reject(new Error('User not found'));
    return delay(mockUserFills(user, params));
  },

  userStrategyChat(userId: string, strategyId: string): Promise<AdminStrategyChat> {
    return delay(mockStrategyChat(userId, strategyId));
  },

  addAiCredits(id: string, amount: number): Promise<{ balance: number }> {
    aiCreditsGranted.set(id, (aiCreditsGranted.get(id) ?? 0) + amount);
    recordAdminAction(id, 'CreditsGranted', 'credits', `+${amount} credits`);
    return delay({ balance: 10 + (aiCreditsGranted.get(id) ?? 0) }, 300);
  },
};

function cmp(a: AdminUserRow, b: AdminUserRow, key: UsersSortBy): number {
  const numericKeys: UsersSortBy[] = ['trades', 'volume', 'netPnl', 'gains', 'losses'];
  const av = a[key];
  const bv = b[key];
  if (numericKeys.includes(key)) return Number(av ?? 0) - Number(bv ?? 0);
  if (key === 'createdAt' || key === 'lastActivityAt' || key === 'lastLoginAt') {
    return new Date((av as string) ?? 0).getTime() - new Date((bv as string) ?? 0).getTime();
  }
  return String(av ?? '').localeCompare(String(bv ?? ''));
}
